// material
import {
  Chip,
  CircularProgress,
  Container,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  Tooltip
} from '@mui/material';
// hooks
import useSettings from '../../hooks/useSettings';
// components
import Page from '../../components/Page';
import HeaderBreadcrumbs from '../../components/HeaderBreadcrumbs';
import { PATH_DASHBOARD } from '../../routes/paths';
import { checkRoles, ROLE_USER } from 'guards/CheckRoles';
import { generatePath, useNavigate, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import axios from '../../utils/axios';
import { Fragment, useEffect, useState } from 'react';
import DeviceGroupForm from '../../components/device-groups/DeviceGroupForm';
import { MIconButton } from '../../components/@material-extend';
import { Icon } from '@iconify/react';
import closeFill from '@iconify/icons-eva/close-fill';
import { useSnackbar } from 'notistack';
import LoadingSpinner from '../../components/LoadingSpinner';
import {
  addDeviceTag,
  getProfile,
  startGlobalLoading,
  stopGlobalLoading
} from '../../redux/slices/user';
import { useDispatch, useSelector } from 'react-redux';
import { RootState } from '../../redux/store';
import { Building, DeviceTag } from '../../@types/model/user';
import { useRecoilValue, useSetRecoilState } from 'recoil';
import { buildingState, getBuildings } from 'recoil/buildingState';
import { LoadingButton } from '@mui/lab';
import { deviceState, getDevices } from 'recoil/deviceState';
import { Device } from '../../@types/model/device';
import '../../assets/css/deviceGroupsBuildingsDevices.scss';
import BuildingAssignDeviceModal from './partials/BuildingAssignDeviceModal';
import BuildingCreateDeviceModal from './partials/BuildingCreateDeviceModal';
import BuildingNewModal from './partials/BuildingNewModal';
import DeviceTagAssignBuildingModal from './partials/DeviceTagAssignBuildingModal';
import SvgIconStyle from 'components/SvgIconStyle';
import ConfirmDialog from 'components/ConfirmDialog';
import BasicModal from 'components/BasicModal';
import Typography from '@mui/material/Typography';
import ShareWithUserForm from 'components/ShareWithUserForm';
import shareDevice from 'components/device/Components/shareDevice';

// ----------------------------------------------------------------------

export default function EditDeviceGroupBuildingDevice() {
  const { themeStretch } = useSettings();
  const { t } = useTranslation(['translation', 'custom']);
  const navigate = useNavigate();
  const { enqueueSnackbar, closeSnackbar } = useSnackbar();
  const applicationDispatch = useDispatch();
  const { loaded, deviceTags, user } = useSelector((state: RootState) => state.user);
  const [deviceGroup, setDeviceGroup] = useState<DeviceTag | null>(null);
  const { guid } = useParams();
  const [saving, setSaving] = useState<Boolean>(false);
  const [buildingToUnlink, setBuildingToUnlink] = useState<Building | null>(null);
  const [deviceToUnlink, setDeviceToUnlink] = useState<Device | null>(null);
  const [deviceBuildingToUnlink, setDeviceBuildingToUnlink] = useState<Building | null>(null);
  const [deviceToShare, setDeviceToShare] = useState<Device | null>(null);
  const [shareLoading, setShareLoading] = useState(false);

  const buildings: Building[] = useRecoilValue(buildingState);
  const devices: Device[] = useRecoilValue(deviceState);
  const setDevices = useSetRecoilState(deviceState);
  const setBuildings = useSetRecoilState(buildingState);

  const [buildingAssignDevice, setBuildingAssignDevice] = useState<Building | null>(null);
  const [deviceTagAssignBuilding, setDeviceTagAssignBuilding] = useState<DeviceTag | null>(null);
  const [buildingCreateDevice, setBuildingCreateDevice] = useState<Building | null>(null);
  const [newBuildingModal, setNewBuildingModal] = useState<boolean>(false);

  const role = checkRoles(user, ROLE_USER) ? '/' : '/end-user';

  useEffect(() => {
    if (loaded) {
      setDeviceGroup(deviceTags.find((dt: DeviceTag) => dt.guid === guid)!);
    }
    getDevices().then((devices: Device[]) => {
      setDevices(devices);
    });
    if (buildings.length === 0) {
      getBuildings().then((buildings: Building[]) => {
        setBuildings(buildings);
      });
    }
  }, [deviceTags]);

  const saveDeviceGroup = (deviceGroup: DeviceTag) => {
    axios
      .put(`/api/v2/device-tag/${deviceGroup.guid}`, {
        ...deviceGroup,
        attributes: {
          ...deviceGroup.attributes,
          'delivery-configurations': deviceGroup.attributes.delivery_configurations
        }
      })
      .then(() => {
        setDeviceGroup(deviceGroup);
        enqueueSnackbar(t('device_group_updated'), {
          variant: 'success',
          action: (key) => (
            <MIconButton size="small" onClick={() => closeSnackbar(key)}>
              <Icon icon={closeFill} />
            </MIconButton>
          )
        });

        applicationDispatch(getProfile());
        navigate(generatePath(PATH_DASHBOARD.deviceGroups.list));
      });
  };
  const pageTitle = t('edit_group_device', { ns: 'custom' });
  return (
    <Page title={pageTitle}>
      <Container maxWidth={themeStretch ? false : 'xl'}>
        <HeaderBreadcrumbs
          heading={pageTitle}
          links={[
            { name: t('home'), href: role },
            { name: t('device_groups', { ns: 'custom' }), href: PATH_DASHBOARD.deviceGroups.list },
            { name: pageTitle, href: PATH_DASHBOARD.deviceGroups.new }
          ]}
        />
        {!loaded || !deviceGroup ? (
          <LoadingSpinner />
        ) : (
          <DeviceGroupForm deviceGroup={deviceGroup} edit={true} submitHandler={saveDeviceGroup} />
        )}
        <div className="buildings">
          {saving ? (
            <div className="circular_progress">
              <CircularProgress size={26} />
            </div>
          ) : null}
          <div>
            {buildings
              .filter((building: Building) => {
                if (building.attributes.parent === guid) return true;
                return false;
              })
              .map((building: Building) => {
                let building_guid = building.guid;
                if (building.attributes.is_ref_of) building_guid = building.attributes.is_ref_of;
                return (
                  <div key={building.guid} className="building">
                    <h3>
                      {building.attributes.name}
                      <Tooltip title={t('edit').toString()} placement="bottom">
                        <Chip
                          className="editBuilding"
                          icon={
                            <SvgIconStyle
                              src="/static/icons/giraf/edit.svg"
                              sx={{ width: '20px', height: '20px' }}
                            />
                          }
                          // key={`${tagEntity.guid}-${dg.guid}`}
                          // label={tagEntity.attributes.name}
                          size={'small'}
                          color="default"
                          onClick={() => {
                            navigate(
                              generatePath(PATH_DASHBOARD.buildings.edit, { guid: building.guid })
                            );
                          }}
                        />
                      </Tooltip>
                    </h3>
                    <div className="devices">
                      <h4>{t('devices')}</h4>
                      {devices
                        .filter((d: Device) => {
                          if (d.attributes.tags.includes(building_guid)) return true;
                          else return false;
                        })
                        .map((d: Device) => {
                          return (
                            <div className="device" key={d.guid}>
                              {d.attributes.name}
                              <Tooltip title={t('share').toString()} placement="bottom">
                                <Chip
                                  className="unlinkDevice"
                                  icon={
                                    <SvgIconStyle
                                      src="/static/icons/giraf/share.svg"
                                      sx={{ width: '25px', height: '25px' }}
                                    />
                                  }
                                  // key={`${tagEntity.guid}-${dg.guid}`}
                                  // label={tagEntity.attributes.name}
                                  size={'small'}
                                  color="info"
                                  onClick={() => {
                                    setDeviceToShare(d);
                                  }}
                                />
                              </Tooltip>
                              <Tooltip title={t('unlink').toString()} placement="bottom">
                                <Chip
                                  className="unlinkDevice"
                                  icon={
                                    <SvgIconStyle
                                      src="/static/icons/giraf/link-slash.svg"
                                      sx={{ width: '25px', height: '25px' }}
                                    />
                                  }
                                  // key={`${tagEntity.guid}-${dg.guid}`}
                                  // label={tagEntity.attributes.name}
                                  size={'small'}
                                  color="error"
                                  onClick={() => {
                                    setDeviceToUnlink(d);
                                    setDeviceBuildingToUnlink(building);
                                  }}
                                />
                              </Tooltip>
                            </div>
                          );
                        })}
                      <div className="options">
                        <LoadingButton
                          fullWidth
                          size="small"
                          type="submit"
                          variant="contained"
                          sx={{ my: 2, maxWidth: '280px', marginRight: '10px' }}
                          onClick={() => {
                            setBuildingCreateDevice(building);
                          }}
                        >
                          {t('add_device_to_this_building')}
                        </LoadingButton>
                        <LoadingButton
                          fullWidth
                          size="small"
                          type="submit"
                          variant="outlined"
                          sx={{ my: 2, maxWidth: '180px' }}
                          onClick={() => {
                            setBuildingAssignDevice(building);
                          }}
                        >
                          {t('assign_device')}
                        </LoadingButton>
                      </div>
                    </div>

                    <Tooltip title={t('unlink').toString()} placement="bottom">
                      <Chip
                        className="unlink"
                        icon={
                          <SvgIconStyle
                            src="/static/icons/giraf/link-slash.svg"
                            sx={{ width: '25px', height: '25px' }}
                          />
                        }
                        // key={`${tagEntity.guid}-${dg.guid}`}
                        // label={tagEntity.attributes.name}
                        size={'small'}
                        color="error"
                        onClick={() => {
                          setBuildingToUnlink(building);
                        }}
                      />
                    </Tooltip>
                  </div>
                );
              })}
          </div>
        </div>
        <LoadingButton
          fullWidth
          size="small"
          type="submit"
          variant="contained"
          sx={{ my: 2, maxWidth: '200px' }}
          onClick={() => {
            setNewBuildingModal(true);
          }}
        >
          {t('add_building')}
        </LoadingButton>
        <LoadingButton
          fullWidth
          size="small"
          type="submit"
          variant="outlined"
          sx={{ my: 2, maxWidth: '200px', marginLeft: '10px' }}
          onClick={() => {
            setDeviceTagAssignBuilding(deviceGroup);
          }}
        >
          {t('assign_building')}
        </LoadingButton>
      </Container>
      <BuildingAssignDeviceModal
        buildingAssignDevice={buildingAssignDevice}
        buildings={buildings}
        setBuildingAssignDevice={(building: Building | null) => setBuildingAssignDevice(building)}
      />
      <BuildingCreateDeviceModal
        deviceTag={guid}
        buildingCreateDevice={buildingCreateDevice}
        setBuildingCreateDevice={(building: Building | null) => setBuildingCreateDevice(building)}
      />
      <DeviceTagAssignBuildingModal
        deviceTagAssignBuilding={deviceTagAssignBuilding}
        buildings={buildings}
        setDeviceTagAssignBuilding={(deviceTag: DeviceTag | null) => {
          setDeviceTagAssignBuilding(deviceTag);
        }}
        setSaving={(s: Boolean) => {
          setSaving(s);
        }}
      />
      <BuildingNewModal
        deviceTag={guid !== undefined ? guid : ''}
        newBuildingModal={newBuildingModal}
        setNewBuildingModal={(newBuildingModal: boolean) => {
          setNewBuildingModal(newBuildingModal);
          setSaving(true);
          getBuildings().then((buildings: Building[]) => {
            setBuildings(buildings);
            setSaving(false);
          });
        }}
      />
      <ConfirmDialog
        title={t('unlink_building')}
        open={buildingToUnlink ? true : false}
        setOpen={setBuildingToUnlink}
        onConfirm={() => {
          setSaving(true);
          axios
            .put(`/api/v2/device-tag/${buildingToUnlink?.guid}`, {
              ...buildingToUnlink,
              attributes: {
                ...buildingToUnlink?.attributes,
                parent: null
              }
            })
            .then(() => {
              enqueueSnackbar(t('building_unlink_success'), {
                variant: 'success',
                action: (key) => (
                  <MIconButton size="small" onClick={() => closeSnackbar(key)}>
                    <Icon icon={closeFill} />
                  </MIconButton>
                )
              });
              setBuildings(
                buildings.map((building: Building) => {
                  if (building.guid === buildingToUnlink?.guid) {
                    return {
                      ...building,
                      attributes: {
                        ...building?.attributes,
                        parent: ''
                      }
                    };
                  }
                  return building as Building;
                })
              );
              setSaving(false);
            })
            .catch(() => {});
        }}
      >
        {t('confirm_unlink_building')}
      </ConfirmDialog>
      <ConfirmDialog
        title={t('unlink_device')}
        open={deviceToUnlink && deviceBuildingToUnlink ? true : false}
        setOpen={setDeviceToUnlink}
        onConfirm={() => {
          setSaving(true);
          let newTags: string[] = [];
          deviceToUnlink?.attributes.tags.forEach((tag) => {
            if (tag !== deviceBuildingToUnlink?.guid) newTags.push(tag);
          });
          axios
            .put(`/api/v2/device/${deviceToUnlink?.guid}`, {
              name: deviceToUnlink?.attributes.name,
              state: deviceToUnlink?.attributes.state,
              tags: newTags,
              last_location: deviceToUnlink?.attributes.last_location,
              parameters: deviceToUnlink?.attributes.parameters
            })
            .then(() => {
              enqueueSnackbar(t('device_unlinked_to_building'), {
                variant: 'success',
                action: (key) => (
                  <MIconButton size="small" onClick={() => closeSnackbar(key)}>
                    <Icon icon={closeFill} />
                  </MIconButton>
                )
              });

              setDevices(
                devices.map((device) => {
                  if (device.guid === deviceToUnlink?.guid) {
                    return {
                      ...device,
                      attributes: {
                        ...device.attributes,
                        tags: newTags
                      }
                    };
                  }
                  return device;
                })
              );
              setSaving(false);
            });
        }}
      >
        {t('confirm_unlink_device')}
      </ConfirmDialog>
      <BasicModal
        open={deviceToShare !== null}
        handleClose={() => {
          setDeviceToShare(null);
        }}
      >
        {deviceToShare ? (
          <Fragment>
            <Typography id="modal-modal-title" variant="h6" component="h2">
              {t('share')} {deviceToShare.attributes.name} ({deviceToShare.attributes.identifier})
            </Typography>
            <ShareWithUserForm
              submitHander={(name: string, email: string) => {
                setShareLoading(true);
                shareDevice(
                  deviceToShare.guid,
                  name,
                  email,
                  () => {
                    setShareLoading(false);
                    setDeviceToShare(null);
                    enqueueSnackbar(t('device_shared_successfuly'), { variant: 'success' });
                  },
                  (error) => {
                    setShareLoading(false);
                    enqueueSnackbar(error.message, { variant: 'error' });
                  }
                );
              }}
              loading={shareLoading}
            />
          </Fragment>
        ) : null}
      </BasicModal>
    </Page>
  );
}
